import axios from 'axios';
import { API_BASE_URL } from 'configs/AppConfig';
import { signOutSuccess } from 'store/slices/authSlice';
import store from '../store';
import { AUTH_TOKEN, REFRESH_TOKEN } from 'constants/AuthConstant';
import { notification } from 'antd';

const service = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000
});

let isRefreshing = false;
let failedQueue = [];
const REFRESH_LOCK_KEY = 'cf_refresh_lock';
const REFRESH_LOCK_TTL_MS = 10000;

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

const forceLogout = () => {
  localStorage.removeItem(AUTH_TOKEN);
  localStorage.removeItem(REFRESH_TOKEN);
  localStorage.removeItem('auth_user');
  localStorage.removeItem(REFRESH_LOCK_KEY);
  store.dispatch(signOutSuccess());
};

const acquireRefreshLock = () => {
  try {
    const now = Date.now();
    const raw = localStorage.getItem(REFRESH_LOCK_KEY);
    if (raw) {
      const lock = JSON.parse(raw);
      if (lock?.until > now) return false;
    }
    localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify({ until: now + REFRESH_LOCK_TTL_MS }));
    return true;
  } catch {
    return true;
  }
};

const releaseRefreshLock = () => {
  try {
    localStorage.removeItem(REFRESH_LOCK_KEY);
  } catch {
    // ignore
  }
};

const waitForOtherTabRefresh = () =>
  new Promise((resolve, reject) => {
    const started = Date.now();
    const tick = () => {
      const token = localStorage.getItem(AUTH_TOKEN);
      const lock = localStorage.getItem(REFRESH_LOCK_KEY);
      if (!lock && token) {
        resolve(token);
        return;
      }
      if (Date.now() - started > REFRESH_LOCK_TTL_MS + 500) {
        reject(new Error('Refresh wait timed out'));
        return;
      }
      setTimeout(tick, 120);
    };
    tick();
  });

service.interceptors.request.use(config => {
  const jwtToken = localStorage.getItem(AUTH_TOKEN);
  if (jwtToken) {
    config.headers['Authorization'] = `Bearer ${jwtToken}`;
  }
  return config;
}, error => {
  notification.error({ message: 'Error' });
  return Promise.reject(error);
});

service.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const originalRequest = error.config;

    if (!error.response) {
      notification.error({ message: 'Network Error', description: 'Please check your internet connection' });
      return Promise.reject(error);
    }

    const status = error.response.status;

    if (status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN);

      if (!refreshToken) {
        forceLogout();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return service(originalRequest);
        });
      }

      // Another tab may already be refreshing.
      if (!acquireRefreshLock()) {
        try {
          const token = await waitForOtherTabRefresh();
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          originalRequest._retry = true;
          return service(originalRequest);
        } catch {
          // Fall through and try refresh in this tab.
        }
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken });
        const newToken = data.token || data.accessToken;
        const newRefreshToken = data.refreshToken;

        if (newToken) {
          localStorage.setItem(AUTH_TOKEN, newToken);
          if (newRefreshToken) localStorage.setItem(REFRESH_TOKEN, newRefreshToken);
          if (data.user) localStorage.setItem('auth_user', JSON.stringify(data.user));

          service.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          processQueue(null, newToken);

          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return service(originalRequest);
        }
        processQueue(error);
        forceLogout();
        return Promise.reject(error);
      } catch (refreshError) {
        processQueue(refreshError);
        // Rate-limit / transient errors should not wipe the session.
        const refreshStatus = refreshError?.response?.status;
        if (refreshStatus === 429 || refreshStatus >= 500 || !refreshError?.response) {
          return Promise.reject(refreshError);
        }
        forceLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
        releaseRefreshLock();
      }
    }

    let notificationParam = { message: '' };

    if (status === 400) {
      notificationParam.message = 'Bad Request';
      notificationParam.description = error.response?.data?.message || 'Invalid request data';
    }
    if (status === 403) {
      notificationParam.message = 'Forbidden';
      notificationParam.description = 'You do not have permission';
    }
    if (status === 404) notificationParam.message = 'Not Found';
    if (status === 500) notificationParam.message = 'Internal Server Error';
    if (status === 508) notificationParam.message = 'Time Out';

    if (notificationParam.message) notification.error(notificationParam);

    return Promise.reject(error);
  }
);

export default service;
